// LicenseWall — full-page license activation screen shown when no valid license
// key is configured. Replaces the entire app shell until a key is activated.
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Tooltip,
  message,
} from "antd";
import {
  CopyOutlined,
  KeyOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { api, type LicenseStatus } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// ── Installation ID display ────────────────────────────────────────────────

function InstallationIDBox({ id }: { id: string }) {
  const copy = () => {
    navigator.clipboard.writeText(id).then(
      () => message.success("Installation ID copied!"),
      () => message.error("Copy failed — select and copy manually"),
    );
  };
  return (
    <div
      style={{
        background: "rgba(54,197,240,0.06)",
        border: "1px solid rgba(54,197,240,0.2)",
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: "#36c5f0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        🔑 Installation ID
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <code
          style={{
            fontSize: 15,
            letterSpacing: 3,
            color: "#5ee0ff",
            userSelect: "all",
            flex: 1,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            background: "rgba(0,0,0,0.3)",
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid rgba(54,197,240,0.15)",
          }}
        >
          {id}
        </code>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={copy}
          style={{
            background: "rgba(54,197,240,0.12)",
            border: "1px solid rgba(54,197,240,0.25)",
            color: "#36c5f0",
            borderRadius: 6,
            flexShrink: 0,
          }}
        >
          Copy
        </Button>
      </div>
      <div style={{ fontSize: 11, color: "#8592b8", marginTop: 8 }}>
        Gửi ID này cho vendor để nhận license key tương ứng với installation này.
      </div>
    </div>
  );
}

// ── Main wall ─────────────────────────────────────────────────────────────

interface LicenseWallProps {
  status: LicenseStatus;
  onActivated: (s: LicenseStatus) => void;
}

function LicenseWallCard({ status, onActivated }: LicenseWallProps) {
  const [key, setKey]       = useState("");
  const [saving, setSaving] = useState(false);
  const { user, logout }    = useAuth();
  const nav                 = useNavigate();

  const handleActivate = async () => {
    const trimmed = key.trim();
    if (!trimmed) {
      message.error("Vui lòng paste license key vào ô bên dưới");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.activateLicense(trimmed);
      message.success("License activated successfully!");
      onActivated(updated);
    } catch (e) {
      if (e instanceof Error) message.error("Activation failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 560,
        background: "rgba(11,16,32,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(54,197,240,0.2)",
        borderRadius: 16,
        padding: "32px 36px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(54,197,240,0.06)",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ marginBottom: 14 }}>
          <SafetyCertificateOutlined
            style={{ fontSize: 44, color: "#36c5f0" }}
          />
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#ffffff", marginBottom: 8 }}>
          Kích hoạt License
        </div>
        <div style={{ fontSize: 13, color: "#8592b8" }}>
          {status.message?.includes("Trial expired")
            ? "Thời gian dùng thử đã hết. Vui lòng liên hệ vendor để nhận license key."
            : "Hệ thống chưa có license key. Vui lòng liên hệ vendor để nhận key."}
        </div>
      </div>

      {/* Alert */}
      <Alert
        type="warning"
        showIcon
        message={status.message || "Không có license — hệ thống bị khóa cho đến khi kích hoạt."}
        style={{
          marginBottom: 20,
          fontSize: 13,
          background: "rgba(247,185,85,0.08)",
          border: "1px solid rgba(247,185,85,0.2)",
          borderRadius: 8,
          color: "#f7b955",
        }}
      />

      {/* Installation ID */}
      {status.installation_id && (
        <InstallationIDBox id={status.installation_id} />
      )}

      {/* Key input */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#8592b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Paste license key vào đây:
        </div>
        <Input.TextArea
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Paste SFLOW-… license key here"
          rows={4}
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            marginBottom: 12,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(54,197,240,0.15)",
            borderRadius: 8,
            color: "#dde4f5",
            resize: "vertical",
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleActivate();
          }}
        />
        <Button
          type="primary"
          icon={<KeyOutlined />}
          onClick={handleActivate}
          loading={saving}
          disabled={!key.trim()}
          block
          size="large"
          style={{
            height: 44,
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            background: "linear-gradient(135deg, #36c5f0 0%, #00d4a3 100%)",
            border: "none",
            color: "#001018",
            boxShadow: "0 8px 24px rgba(54,197,240,0.3)",
          }}
        >
          Activate
        </Button>
      </div>

      {/* Footer: logged-in user + logout */}
      {user && (
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid rgba(54,197,240,0.12)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "#8592b8" }}>
            Đăng nhập với tư cách:{" "}
            <strong style={{ color: "#dde4f5" }}>{user.username}</strong>{" "}
            <span style={{ color: "#36c5f0", fontSize: 11 }}>({user.role})</span>
          </span>
          <Tooltip title="Logout">
            <Button
              size="small"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                background: "rgba(255,93,108,0.1)",
                border: "1px solid rgba(255,93,108,0.2)",
                color: "#ff8f9a",
                borderRadius: 6,
              }}
            >
              Logout
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ── Page wrapper (centered full screen) ──────────────────────────────────

export default function LicenseWall({
  status,
  onActivated,
}: LicenseWallProps) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(900px 600px at 80% -5%, rgba(54,197,240,.10), transparent 60%), " +
          "radial-gradient(800px 600px at -10% 40%, rgba(0,212,163,.07), transparent 60%), " +
          "#070a14",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <div
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(135deg, #36c5f0, #00d4a3)",
            borderRadius: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 20px rgba(54,197,240,0.3)",
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#001018" strokeWidth="2.2" strokeLinecap="round">
            <path d="M2 12h4l3-7 4 14 3-7h6"/>
          </svg>
        </div>
        <span style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, letterSpacing: 0.3, fontFamily: "Inter, sans-serif" }}>
          NetScope Console
        </span>
      </div>

      <LicenseWallCard status={status} onActivated={onActivated} />
    </div>
  );
}

// ── LicenseGate: wraps the main Shell, shows LicenseWall when unlicensed ──

interface LicenseGateProps {
  children: React.ReactNode;
}

export function LicenseGate({ children }: LicenseGateProps) {
  const [status, setStatus]   = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.licenseStatus()
      .then(setStatus)
      .catch(() => {
        setStatus(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  if (status && !status.valid) {
    return (
      <LicenseWall
        status={status}
        onActivated={(updated) => setStatus(updated)}
      />
    );
  }

  return <>{children}</>;
}
